// formSubmit.js — pure helpers + the network seam for the Form block's LIVE
// (ViewWheel) submission. Gated by FORM_BLOCK_SUBMIT_ENABLED; with the flag OFF
// this module is never imported at runtime (FormBlock branches before it).
//
// The BE form-response endpoint SHIPPED (Backend yuga_wheel FormSubmitView, mounted
// in yuga_wheel/urls.py). It is public (AllowAny), owner-tier premium-gated
// server-side, and takes the wheel id + block id in the URL PATH with the answers
// as an OBJECT keyed by field id:
//   POST /wheel/forms/form/<wheel_id>/<block_id>/submit/   body: { answers: {id: value}, recaptcha_token? }
//   -> 201 {ok:true} stored · 403 upgrade (owner not premium) · 400 empty/invalid
// The original FE contract here guessed a different shape (POST wheel/forms/response/
// with the ids in the body and answers as a list) which the BE never implemented,
// so every live submit 404'd into the block's error state. This module now matches
// the shipped BE contract exactly.
//
// Anti-spam: the app's existing forms path (services/mutations.js
// useSubmitSupportRequest) uses a reCAPTCHA token and there is NO honeypot in the
// app today, so we add a lightweight, self-contained honeypot here (no site key,
// no BE dependency) AND keep a recaptcha_token seam on the payload for the BE
// owner to require later.

import { isValidPhoneNumber } from "react-phone-number-input";
import API from "../../../../services/request";

// ISP-9999 (item 2): "Long answer" 200-character limit — defence in depth
// alongside the <textarea maxLength> the control itself already enforces.
export const LONGTEXT_MAX = 200;

// The shipped BE endpoint: ids in the PATH. See Backend yuga_wheel/urls.py
// (path 'forms/form/<int:wheel_id>/<str:block_id>/submit/', name='form_submit').
export const submitFormResponsePath = (wheelId, blockId) =>
  `wheel/forms/form/${wheelId}/${blockId}/submit/`;

// A plausible-looking but always-hidden input a human never sees; bots that
// auto-fill every field trip it. Kept out of the wire payload.
export const HONEYPOT_FIELD = "company_website";

export const isEmail = (v) =>
  typeof v === "string" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

// A field's stored value counts as empty for validation. Checkboxes are empty
// unless explicitly ticked (true).
const isEmpty = (field, value) => {
  if (field.type === "checkbox") return value !== true;
  return value == null || String(value).trim() === "";
};

// Pure. Returns { [fieldId]: errorMessage } for every invalid field; an empty
// object means valid. Unknown types are treated as text. Dropdowns are NOT
// required-enforced — ISP-9999 added a real per-field options editor, but
// changing dropdown required-enforcement was never one of that note's nine
// items, so an empty dropdown still never becomes an unsatisfiable blocker.
export const validateForm = (fields = [], values = {}) => {
  const errors = {};
  fields.forEach((f) => {
    if (!f || f.id == null) return;
    const v = values[f.id];
    if (f.required && f.type !== "dropdown" && isEmpty(f, v)) {
      errors[f.id] =
        f.type === "checkbox"
          ? "Please tick this box."
          : "This field is required.";
      return;
    }
    if (f.type === "email" && !isEmpty(f, v) && !isEmail(v)) {
      errors[f.id] = "Enter a valid email address.";
      return;
    }
    // ISP-9999 (item 8): validate the phone field's stored E.164 value.
    if (f.type === "phone" && !isEmpty(f, v)) {
      let validPhone = false;
      try {
        validPhone = isValidPhoneNumber(String(v));
      } catch (e) {
        validPhone = false;
      }
      if (!validPhone) {
        errors[f.id] = "Enter a valid phone number.";
        return;
      }
    }
    // ISP-9999 (item 2): defence in depth for "Long answer" — the <textarea
    // maxLength> attribute already blocks this client-side; this catches a
    // value that reaches validateForm some other way (e.g. a pasted value on
    // an engine that does not enforce maxlength on paste).
    if (f.type === "longtext" && !isEmpty(f, v) && String(v).length > LONGTEXT_MAX) {
      errors[f.id] = `Keep it under ${LONGTEXT_MAX} characters.`;
    }
  });
  return errors;
};

// Pure. True when the honeypot was filled (=> treat as spam and drop silently).
export const isHoneypotFilled = (values = {}) =>
  String(values[HONEYPOT_FIELD] ?? "").trim().length > 0;

// Pure. Build the request descriptor the network seam consumes. `answers` is an
// OBJECT keyed by field id (checkbox -> boolean, everything else -> trimmed
// string) to match the shipped BE contract; the honeypot is never a field so it
// never appears. wheelId + blockId travel in the URL (not the body); recaptchaToken
// is an optional seam for the BE owner. Extra props (segmentId/title, unused by the
// BE) are ignored.
export const buildResponsePayload = ({
  wheelId,
  blockId,
  fields = [],
  values = {},
  recaptchaToken,
} = {}) => {
  const answers = {};
  fields
    .filter((f) => f && f.id != null)
    .forEach((f) => {
      answers[f.id] =
        f.type === "checkbox"
          ? values[f.id] === true
          : String(values[f.id] ?? "").trim();
    });
  const descriptor = {
    wheelId: wheelId ?? null,
    blockId: blockId ?? null,
    answers,
  };
  if (recaptchaToken) descriptor.recaptcha_token = recaptchaToken;
  return descriptor;
};

// Network seam (thin, mirrors the mutations.js API.post pattern). Not pure and
// not unit-tested — the pure builders above are. Consumes the descriptor from
// buildResponsePayload: ids -> URL path, answers (+ optional recaptcha_token) -> body.
export const submitFormResponse = ({
  wheelId,
  blockId,
  answers,
  recaptcha_token: recaptchaToken,
} = {}) => {
  const body = { answers };
  if (recaptchaToken) body.recaptcha_token = recaptchaToken;
  return API.post(submitFormResponsePath(wheelId, blockId), body);
};
